import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Wallet, Calendar, ShieldCheck, Star, MoreVertical, ExternalLink, XCircle, CheckCircle2 } from 'lucide-react';
import { agenciesAPI } from '../utils/api';
import constant from '../constants/constant';

const AgencyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3000);
  };

  const [confirmModal, setConfirmModal] = useState({ isOpen: false, status: '' });

  const token = sessionStorage.getItem('adminToken');
  const userinfo = React.useMemo(() => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [token]);

  const userType = userinfo?.user_type || 'ADMIN';
  const userId = userinfo?.id;
  const isAuthorizedToApprove = 
    userType === 'SUPER_ADMIN' || 
    userType === 'ADMIN' || 
    (userType === 'BD' && agency?.bd_user_id === userId) ||
    (userType === 'MANAGER' && (!agency?.bd_user_id || agency?.bd_manager_id === userId));

  useEffect(() => {
    fetchAgencyDetails();
  }, [id]);

  const fetchAgencyDetails = async () => {
    setLoading(true);
    try {
      const response = await agenciesAPI.getAgency(id);
      setAgency(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching agency details:', err);
      setError('Failed to load agency details.');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const baseUrl = constant.API_BASE_URL.replace('/api/', '');
    return `${baseUrl}${path}`;
  };

  const confirmStatusUpdate = (status) => {
    setConfirmModal({ isOpen: true, status });
  };

  const handleStatusUpdate = async () => {
    const newStatus = confirmModal.status;
    if (!newStatus) return;
    
    setConfirmModal({ isOpen: false, status: '' });
    try {
      await agenciesAPI.updateAgencyStatus(id, newStatus);
      setAgency({ ...agency, status: newStatus });
      showToast(`Agency status updated to ${newStatus.toUpperCase()}`);
    } catch (err) {
      console.error('Error updating status:', err);
      showToast('Failed to update status', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !agency) {
    return (
      <div className="text-center p-20">
        <p className="text-red-500 text-lg font-bold">{error || 'Agency not found'}</p>
        <button onClick={() => navigate('/agencies')} className="mt-4 text-pink-600 underline">Back to Agencies</button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/agencies')}
            className="p-2 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-pink-600 transition"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="text-3xl font-bold text-slate-800">{agency.agency_name}</h2>
            <div className="flex items-center gap-2 text-gray-400 text-sm mt-1">
              <span className="px-2 py-0.5 bg-slate-100 rounded text-slate-500 font-bold uppercase text-[10px]">Agency ID: #{agency.id.substring(0, 8)}</span>
              <span>•</span>
              <Calendar size={14} />
              <span>Joined {new Date(agency.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          {isAuthorizedToApprove ? (
            agency.status === 'pending' ? (
              <>
                <button 
                  onClick={() => confirmStatusUpdate('approved')}
                  className="px-6 py-2.5 bg-green-500 text-white rounded-xl font-bold hover:bg-green-600 transition shadow-lg shadow-green-200 flex items-center gap-2"
                >
                  <ShieldCheck size={18} /> Approve Agency
                </button>
                <button 
                  onClick={() => confirmStatusUpdate('rejected')}
                  className="px-6 py-2.5 bg-red-50 text-red-600 border border-red-100 rounded-xl font-bold hover:bg-red-100 transition flex items-center gap-2"
                >
                  <XCircle size={18} /> Reject
                </button>
              </>
            ) : (
              <div className={`px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 ${
                agency.status === 'approved' ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'
              }`}>
                {agency.status === 'approved' ? <ShieldCheck size={18} /> : <XCircle size={18} />}
                Status: {agency.status.toUpperCase()}
                <button 
                  onClick={() => confirmStatusUpdate('pending')}
                  className="ml-4 text-[10px] underline uppercase tracking-widest opacity-50 hover:opacity-100"
                >
                  Reset to Pending
                </button>
              </div>
            )
          ) : (
            <div className={`px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 ${
              agency.status === 'approved' ? 'bg-green-50 text-green-600 border border-green-100' : 
              agency.status === 'rejected' ? 'bg-red-50 text-red-600 border border-red-100' : 
              'bg-yellow-50 text-yellow-600 border border-yellow-100'
            }`}>
              {agency.status === 'approved' ? <ShieldCheck size={18} /> : agency.status === 'rejected' ? <XCircle size={18} /> : null}
              Status: {(agency.status || 'pending').toUpperCase()}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Profile & Stats */}
        <div className="lg:col-span-1 space-y-6">
          {/* Profile Card */}
          <div className="bg-white rounded-[30px] shadow-xl shadow-gray-200/50 border border-gray-100 p-8 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pink-500 to-purple-600"></div>
            
            <div className="w-32 h-32 mx-auto rounded-3xl overflow-hidden shadow-2xl border-4 border-white mb-6 bg-slate-100 flex items-center justify-center">
              <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-slate-300">
                {agency.agency_name[0]}
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-800">{agency.agency_name}</h3>
            <p className="text-gray-400 font-medium">Agency Owner: <span className="text-slate-600">{agency.creator_name || 'N/A'}</span></p>

            <div className="mt-8 pt-8 border-t border-gray-50 grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50/50 rounded-2xl">
                <Users className="mx-auto mb-2 text-blue-500" size={24} />
                <p className="text-2xl font-black text-blue-600">{agency.hosts_count || 0}</p>
                <p className="text-[10px] uppercase font-bold text-blue-400 tracking-widest">Total Hosts</p>
              </div>
              <div className="p-4 bg-purple-50/50 rounded-2xl">
                <Wallet className="mx-auto mb-2 text-purple-500" size={24} />
                <p className="text-xl font-black text-purple-600">{agency.bd_code || 'NONE'}</p>
                <p className="text-[10px] uppercase font-bold text-purple-400 tracking-widest">BD Code</p>
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="bg-white rounded-[30px] shadow-xl shadow-gray-200/50 border border-gray-100 p-8 space-y-6">
            <h4 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <ShieldCheck size={20} className="text-pink-500" /> Verification Details
            </h4>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Real Name</span>
                <span className="text-slate-700 font-semibold">{agency.real_name || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">WhatsApp</span>
                <span className="text-slate-700 font-semibold">{agency.whatsapp_number || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">National ID</span>
                <span className="text-slate-700 font-semibold">{agency.national_id || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">BD Name</span>
                <span className="text-slate-700 font-semibold">{agency.bd_name || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Status</span>
                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${
                  agency.status === 'approved' ? 'bg-green-100 text-green-600' : 
                  agency.status === 'rejected' ? 'bg-red-100 text-red-600' : 
                  'bg-yellow-100 text-yellow-600'
                }`}>
                  {agency.status || 'pending'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Introduction & Activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* About/Experience */}
          <div className="bg-white rounded-[30px] shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
            <h4 className="text-lg font-bold text-slate-800 mb-4">About Agency</h4>
            <div className="bg-slate-50 rounded-2xl p-6 text-slate-600 leading-relaxed italic">
              "{agency.experience || 'No introduction provided by the agency owner.'}"
            </div>
          </div>

          {/* Attachments / Approval Documents */}
          <div className="bg-white rounded-[30px] shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-lg font-bold text-slate-800">Approval Documents</h4>
              <span className="text-xs text-gray-400 font-medium">{agency.images?.length || 0} Attachments</span>
            </div>
            
            {agency.images && agency.images.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {agency.images.map((img, index) => (
                  <div key={index} className="group relative aspect-square rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition cursor-pointer">
                    <img 
                      src={getImageUrl(img)} 
                      alt={`Document ${index + 1}`} 
                      className="w-full h-full object-cover transition duration-500 group-hover:scale-110" 
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                      <a 
                        href={getImageUrl(img)} 
                        target="_blank" 
                        rel="noreferrer"
                        className="p-2 bg-white rounded-lg text-slate-800 hover:bg-pink-500 hover:text-white transition"
                      >
                        <ExternalLink size={18} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-10 text-center border-2 border-dashed border-gray-100 rounded-2xl">
                <p className="text-gray-400">No documents attached.</p>
              </div>
            )}
          </div>

          {/* Host List Placeholder */}
          <div className="bg-white rounded-[30px] shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
            <div className="p-8 border-b border-gray-50 flex justify-between items-center">
              <h4 className="text-lg font-bold text-slate-800">Agency Hosts</h4>
              <button className="text-pink-600 text-sm font-bold flex items-center gap-1 hover:underline">
                View All <ExternalLink size={14} />
              </button>
            </div>
            
            <div className="p-8 text-center text-gray-400">
              <Star className="mx-auto mb-3 opacity-20" size={48} />
              <p>Host details will be integrated in the next update.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-8">
              <h3 className="text-xl font-bold text-slate-800 mb-2">Confirm Action</h3>
              <p className="text-slate-500 mb-8">
                Are you sure you want to mark this agency as <strong className="text-slate-800 uppercase">{confirmModal.status}</strong>?
              </p>
              <div className="flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setConfirmModal({ isOpen: false, status: '' })}
                  className="px-5 py-2.5 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  onClick={handleStatusUpdate}
                  className="px-5 py-2.5 bg-pink-600 text-white font-bold rounded-xl shadow-lg shadow-pink-500/30 hover:bg-pink-700 transition-all active:scale-95"
                >
                  Confirm Action
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-6 py-3 rounded-xl shadow-lg border text-sm font-bold flex items-center gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300 ${toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-700 border-green-100'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default AgencyDetails;
